import React, { useEffect } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import PropTypes from 'prop-types';
import { Box, CircularProgress } from '@material-ui/core';
import { makeStyles } from '@material-ui/styles';
import {
  fetchCompanyOnboarding,
  companyOnboardingSelector,
  companyOnboardingLoadingSelector,
  companyOnboardingFetchedSelector
} from '../redux/features/company/companiesSlice';
import { APP_PATH_COMPANY_ONBOARDING } from '../config';

const useStyles = makeStyles(() => ({
  loadingContainer: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    minHeight: '100vh'
  }
}));

/**
 * CompanyOnboardingGuard component that checks if a company has completed onboarding.
 * If not completed, redirects to the company onboarding page.
 */
const CompanyOnboardingGuard = ({ children, isCompany }) => {
  const classes = useStyles();
  const history = useHistory();
  const location = useLocation();
  const dispatch = useDispatch();
  const onboarding = useSelector(companyOnboardingSelector);
  const onboardingLoading = useSelector(companyOnboardingLoadingSelector);
  const onboardingFetched = useSelector(companyOnboardingFetchedSelector);

  // Fetch onboarding status once when component mounts
  useEffect(() => {
    // Only check onboarding for companies
    if (!isCompany) {
      return;
    }

    // Don't check if already on onboarding page
    if (location.pathname === APP_PATH_COMPANY_ONBOARDING) {
      return;
    }

    // Only fetch once (check Redux state)
    if (onboardingFetched) {
      return;
    }

    console.log('🔒 [CompanyOnboardingGuard] Fetching onboarding status');
    dispatch(fetchCompanyOnboarding());
  }, [dispatch, isCompany, location.pathname, onboardingFetched]);

  // Redirect to onboarding if needed (based on Redux state)
  useEffect(() => {
    if (
      !isCompany ||
      !onboarding ||
      location.pathname === APP_PATH_COMPANY_ONBOARDING
    ) {
      return;
    }

    const { status } = onboarding;
    console.log('🔒 [CompanyOnboardingGuard] Current status:', status);

    // Redirect to onboarding if not completed or skipped
    if (status === 'NOT_STARTED' || status === 'IN_PROGRESS') {
      console.log('🔒 [CompanyOnboardingGuard] Redirecting to onboarding');
      history.push(APP_PATH_COMPANY_ONBOARDING);
    }
  }, [isCompany, onboarding, location.pathname, history]);

  // Show loading while checking onboarding status
  if (isCompany && onboardingLoading && !onboarding) {
    return (
      <Box className={classes.loadingContainer}>
        <CircularProgress />
      </Box>
    );
  }

  // If on onboarding page, always allow
  if (location.pathname === APP_PATH_COMPANY_ONBOARDING) {
    return children;
  }

  // If onboarding exists and is not completed, show loading (will redirect via useEffect)
  if (
    isCompany &&
    onboarding &&
    (onboarding.status === 'NOT_STARTED' || onboarding.status === 'IN_PROGRESS')
  ) {
    return (
      <Box className={classes.loadingContainer}>
        <CircularProgress />
      </Box>
    );
  }

  // Otherwise, render children
  return children;
};

CompanyOnboardingGuard.propTypes = {
  children: PropTypes.node,
  isCompany: PropTypes.bool
};

export default CompanyOnboardingGuard;
